import { Button, Col, Form, Row } from 'react-bootstrap';
import { useRef, useState } from 'react';
import ReCaptchaV2 from 'react-google-recaptcha';
import axios from 'axios';

import RecaptchaFormAlert from './RecaptchaFormAlert';
import { SITE_KEY, API_URL } from './contactFormConfig';

// The wired-up contact form: reCAPTCHA v2 checkbox, then POST to the API Gateway
// endpoint that fronts the contact Lambda, which verifies the token and sends
// the message on via SendGrid.
//
// Only rendered when contactFormConfig.useLiveContactForm is true, which requires
// both SITE_KEY and API_URL to be set. Do not render this directly -- go through
// Contact.js so the static fallback and error boundary stay in play.
//
// Field names must match what the Lambda reads: name, email, phone, textarea
// (plus company and interest, which it now includes too).
function ContactFormLive() {
  const [captchaPassed, setCaptchaPassed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [show, setShow] = useState(false);
  const [recaptchaSuccess, setRecaptchaSuccess] = useState(false);

  const formRef = useRef(null);
  const recaptchaRef = useRef(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const formData = new FormData(event.target);
    const formDataObj = JSON.stringify(Object.fromEntries(formData.entries()));
    const captchaToken = recaptchaRef.current && recaptchaRef.current.getValue();

    try {
      const res = await axios.post(API_URL, {
        formData: formDataObj,
        captchaToken: captchaToken
      });
      const success = Boolean(res && res.data && res.data.success);
      setRecaptchaSuccess(success);
      if (success && formRef.current) {
        formRef.current.reset();
      }
    } catch (error) {
      setRecaptchaSuccess(false);
    } finally {
      // The token is single-use, so the visitor must re-check the box either way.
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaPassed(false);
      setSubmitting(false);
      setShow(true);
    }
  };

  const handleCaptchaChange = (token) => {
    setCaptchaPassed(Boolean(token));
  };

  return (
    <Form className="contact-form" ref={formRef} onSubmit={handleSubmit}>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="contactName">
            <Form.Label>Name</Form.Label>
            <Form.Control type="text" name="name" placeholder="Your name" required />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="contactCompany">
            <Form.Label>Company</Form.Label>
            <Form.Control type="text" name="company" placeholder="Your company" />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="contactEmail">
            <Form.Label>Email</Form.Label>
            <Form.Control type="email" name="email" placeholder="you@company.com" required />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="contactPhone">
            <Form.Label>Phone</Form.Label>
            <Form.Control type="tel" name="phone" placeholder="Phone number" />
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-3" controlId="contactInterest">
        <Form.Label>What are you interested in?</Form.Label>
        <Form.Select name="interest" defaultValue="">
          <option value="" disabled>
            Select an option
          </option>
          <option>Safety &amp; inspection applications</option>
          <option>Systems integration</option>
          <option>Replacing spreadsheets</option>
          <option>Something else</option>
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-4" controlId="contactMessage">
        <Form.Label>Message</Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          name="textarea"
          placeholder="How can we help?"
          required
        />
      </Form.Group>

      <RecaptchaFormAlert
        show={show}
        recaptchaSuccess={recaptchaSuccess}
        setCloseFunction={() => setShow(false)}
      />

      <div className="contact-recaptcha">
        <ReCaptchaV2
          ref={recaptchaRef}
          sitekey={SITE_KEY}
          onChange={handleCaptchaChange}
          onExpired={() => setCaptchaPassed(false)}
          onErrored={() => setCaptchaPassed(false)}
        />
      </div>

      <Button
        variant=""
        type="submit"
        className="btn-primary-ir"
        disabled={!captchaPassed || submitting}
      >
        {submitting ? 'Sending...' : 'Send Message'}
      </Button>
    </Form>
  );
}

export default ContactFormLive;
